/**
 * 線形回帰分析のための数値計算ライブラリ
 * 最小二乗法による単回帰・重回帰分析の実装
 * 
 * ## 概要
 * 
 * このライブラリは、MIDASアプリケーションで線形回帰分析を実行するための関数を提供します。
 * 
 * ## 主な機能
 * 
 * 1. **linearRegression**: 重回帰分析の実行
 *    - 最小二乗法による係数の推定
 *    - 残差と予測値の計算
 *    - 95%信頼区間の計算
 * 
 * 2. **simpleLinearRegression**: 単回帰分析のヘルパー関数
 *    - 説明変数が1つの場合の簡易インターフェース
 *    - 内部的にlinearRegressionを呼び出す
 * 
 * 3. **predictionInterval**: 予測区間の計算（※現在未使用）
 *    - 新しいデータポイントに対する予測値の信頼区間を計算
 *    - 注意: RegressionPlotコンポーネントでは簡易版を使用しています
 * 
 * ## 使用例
 * 
 * ```typescript
 * // 単回帰分析の例
 * const x = [1, 2, 3, 4, 5];
 * const y = [2, 4, 5, 4, 5];
 * const result = simpleLinearRegression(x, y);
 * logger.info(`y = ${result.coefficients[0]} + ${result.coefficients[1]}x`);
 * 
 * // 重回帰分析の例
 * const X = [[1, 2], [2, 3], [3, 4], [4, 5], [5, 6]];
 * const y = [2, 4, 5, 4, 5];
 * const result = linearRegression(X, y, true); // 切片を含む
 * ```
 * 
 * ## 実装の詳細
 * 
 * - 行列演算: 正規方程式 (X'X)β = X'y を解くことで係数を推定
 * - 行列計算: パフォーマンス最適化のため1次元配列ベースの実装を使用
 * - t分布: 自由度が30以下の場合は簡易近似、それ以上は正規分布で近似
 * - 数値的安定性: 極端に大きいまたは小さい値の場合は注意が必要
 * 
 * ## 既知の問題
 * 
 * - inverseTCDF関数の再帰呼び出しによる無限ループを修正済み（v1.1）
 * - predictionInterval関数は複雑なため、現在はRegressionPlotで簡易版を使用
 * 
 * ## 関連コンポーネント
 * 
 * - LinearRegressionTabContent: 回帰分析の実行と結果表示
 * - RegressionPlot: 単回帰の場合の散布図と回帰直線の可視化
 * - ReportElementRenderer: レポートでの回帰分析結果の表示
 */

import { Matrix } from './matrix-utils';
import { solveByQR, estimateConditionNumber, standardizeData, unstandardizeCoefficients } from './qr-decomposition';
import type { TrainedModel, LinearRegressionParameters } from '../../types/model.types';
import type { DatasetId, ColumnId } from '../../types';
import { createModelId, createDatasetId } from '../../types';
import logger from '../utils/logger';

export interface LinearRegressionResult {
  // 回帰係数（切片を含む）
  coefficients: number[];
  // 残差
  residuals: number[];
  // 予測値
  fittedValues: number[];
  // 回帰係数の95%信頼区間
  confidenceIntervals: Array<{ lower: number; upper: number }>;
  
  // 統計情報
  rSquared: number;
  adjustedRSquared: number;
  standardError: number;
  fStatistic: number;
  pValue: number;
  
  // モデルへの変換メソッド
  toModel(name: string, datasetId: DatasetId | undefined, features: ColumnId[], target: ColumnId | undefined): TrainedModel;
}



/**
 * 線形回帰分析の実行
 * @param X 説明変数行列（各行がサンプル、各列が変数）
 * @param y 目的変数ベクトル
 * @param includeIntercept 切片を含むかどうか
 */
export function linearRegression(
  X: number[][],
  y: number[],
  includeIntercept: boolean = true
): LinearRegressionResult {
  const n = X.length;
  const originalP = X[0].length;
  
  // データの標準化
  const { X_std, means: xMeans, stds: xStds } = standardizeData(X, includeIntercept);
  
  // yの標準化
  const yMean = y.reduce((sum, yi) => sum + yi, 0) / n;
  let yStd = 0;
  for (let i = 0; i < n; i++) {
    const diff = y[i] - yMean;
    yStd += diff * diff;
  }
  yStd = Math.sqrt(yStd / (n - 1));
  if (yStd < 1e-10) yStd = 1; // 定数の場合
  
  const y_std = y.map(yi => (yi - yMean) / yStd);
  
  // Matrixクラスを使用
  const X_mat = Matrix.from2DArray(X_std);
  const y_vec = Matrix.from2DArray([y_std]).transpose();
  
  // 条件数のチェック
  const X_T = X_mat.transpose();
  const X_T_X = X_T.multiply(X_mat);
  const conditionNumber = estimateConditionNumber(X_T_X);
  
  if (conditionNumber > 1e10) {
    logger.warn(`Warning: High condition number (${conditionNumber.toExponential(2)}). Results may be unreliable.`);
  }
  
  // QR分解を使用した解法
  let coefficients_std: number[];
  try {
    const coefficients_mat = solveByQR(X_mat, y_vec);
    coefficients_std = [];
    for (let i = 0; i < coefficients_mat.rows; i++) {
      coefficients_std.push(coefficients_mat.get(i, 0));
    }
  } catch {
    // QR分解が失敗した場合は、フォールバックとして正規方程式を使用
    logger.warn('QR decomposition failed, falling back to normal equations');
    const X_T_X_inv = X_T_X.inverse();
    const X_T_y = X_T.multiply(y_vec);
    const coefficients_mat = X_T_X_inv.multiply(X_T_y);
    coefficients_std = [];
    for (let i = 0; i < coefficients_mat.rows; i++) {
      coefficients_std.push(coefficients_mat.get(i, 0));
    }
  }
  
  // 係数を元のスケールに戻す
  const coefficients = unstandardizeCoefficients(
    coefficients_std,
    xMeans,
    xStds,
    yMean,
    yStd,
    includeIntercept
  );
  
  const p = coefficients.length;
  
  // 予測値と残差の計算（元のスケールで）
  const fittedValues: number[] = [];
  const residuals: number[] = [];
  
  for (let i = 0; i < n; i++) {
    let fitted = includeIntercept ? coefficients[0] : 0;
    for (let j = 0; j < originalP; j++) {
      const coefIndex = includeIntercept ? j + 1 : j;
      fitted += X[i][j] * coefficients[coefIndex];
    }
    fittedValues.push(fitted);
    residuals.push(y[i] - fitted);
  }
  
  // SSEの計算（信頼区間の計算のために必要）
  const SSE = residuals.reduce((sum, r) => sum + r * r, 0);
  
  // 残差の標準誤差（信頼区間の計算のために必要）
  const degreesOfFreedom = n - p;
  const standardError = Math.sqrt(SSE / degreesOfFreedom);
  
  // 係数の標準誤差（信頼区間の計算のために必要）
  // 標準化されたデータで計算し、元のスケールに変換
  const variance = standardError * standardError;
  const coefficientStandardErrors: number[] = [];
  
  // X_T_X_invの計算（標準化されたデータで）
  let X_T_X_inv: Matrix;
  try {
    X_T_X_inv = X_T_X.inverse();
  } catch {
    // 逆行列が計算できない場合は、擬似逆行列を近似
    logger.warn('Matrix inversion failed, using approximation');
    // 対角要素に小さい値を加えて正則化
    const regularized = X_T_X.copy();
    const epsilon = 1e-8;
    for (let i = 0; i < regularized.rows; i++) {
      regularized.set(i, i, regularized.get(i, i) + epsilon);
    }
    X_T_X_inv = regularized.inverse();
  }
  
  // 標準誤差を元のスケールに変換
  if (includeIntercept) {
    // 切片の標準誤差
    coefficientStandardErrors.push(Math.sqrt(X_T_X_inv.get(0, 0) * variance) * yStd);
    // 他の係数の標準誤差
    for (let i = 0; i < originalP; i++) {
      const se_std = Math.sqrt(X_T_X_inv.get(i + 1, i + 1) * variance);
      coefficientStandardErrors.push(se_std * yStd / xStds[i]);
    }
  } else {
    for (let i = 0; i < originalP; i++) {
      const se_std = Math.sqrt(X_T_X_inv.get(i, i) * variance);
      coefficientStandardErrors.push(se_std * yStd / xStds[i]);
    }
  }
  
  // 95%信頼区間の計算
  const alpha = 0.05;
  const tCritical = Math.abs(inverseTCDF(alpha / 2, degreesOfFreedom));
  const confidenceIntervals = coefficients.map((coef, i) => {
    const margin = tCritical * coefficientStandardErrors[i];
    return {
      lower: coef - margin,
      upper: coef + margin
    };
  });
  
  // R² (決定係数) の計算
  const TSS = y.reduce((sum, yi) => {
    const diff = yi - yMean;
    return sum + diff * diff;
  }, 0);
  const rSquared = TSS > 0 ? 1 - SSE / TSS : 0;
  
  // 調整済みR² (Adjusted R²) の計算
  const adjustedRSquared = degreesOfFreedom > 0 
    ? 1 - (SSE / degreesOfFreedom) / (TSS / (n - 1))
    : 0;
  
  // F統計量とp値の計算
  const MSR = includeIntercept ? (TSS - SSE) / (p - 1) : (TSS - SSE) / p; // Mean Square Regression
  const MSE = SSE / degreesOfFreedom; // Mean Square Error
  const fStatistic = MSE > 0 ? MSR / MSE : 0;
  
  // F分布のp値（簡易的な近似）
  const dfRegression = includeIntercept ? p - 1 : p;
  const pValue = fStatistic > 0 ? inverseFCDF(fStatistic, dfRegression, degreesOfFreedom) : 1;
  
  // toModel関数を含むresultオブジェクトを作成
  const result: LinearRegressionResult = {
    coefficients,
    residuals,
    fittedValues,
    confidenceIntervals,
    rSquared,
    adjustedRSquared,
    standardError,
    fStatistic,
    pValue,
    
    // モデルへの変換メソッド
    toModel(name: string, datasetId: DatasetId | undefined, features: ColumnId[], target: ColumnId | undefined): TrainedModel {
      const parameters: LinearRegressionParameters = {
        coefficients: coefficients,
        intercept: includeIntercept ? coefficients[0] : undefined,
        confidenceIntervals: confidenceIntervals
      };
      
      return {
        id: createModelId(crypto.randomUUID()),
        type: 'linear_regression',
        name,
        metadata: {
          createdAt: new Date().toISOString(),
          trainingDatasetId: datasetId || createDatasetId(''),
          features: features.map(f => f as string),
          target: (target as string) || '',
          sampleSize: n
        },
        parameters,
        hyperparameters: {
          includeIntercept
        },
        predictorType: 'linear_regression'
      };
    }
  };
  
  return result;
}

// /**
//  * 予測区間の計算
//  * @param x 説明変数の値
//  * @param result 回帰分析の結果
//  * @param alpha 有意水準（デフォルト0.05で95%予測区間）
//  */
// export function predictionInterval(
//   x: number[],
//   result: LinearRegressionResult,
//   n: number,
//   X_design: number[][],
//   alpha: number = 0.05
// ): { lower: number; upper: number; prediction: number } {
//   // 予測値の計算
//   const x_with_intercept = [1, ...x];
//   const prediction = x_with_intercept.reduce((sum, xi, i) => 
//     sum + xi * result.coefficients[i], 0
//   );
  
//   // X'Xの逆行列が必要（実際の実装では保存しておくべき）
//   const X_T = transpose(X_design);
//   const X_T_X = matrixMultiply(X_T, X_design);
//   const X_T_X_inv = inverse(X_T_X);
  
//   // 予測の標準誤差
//   const x_matrix = [x_with_intercept];
//   const variance_pred = matrixMultiply(
//     matrixMultiply(x_matrix, X_T_X_inv),
//     transpose(x_matrix)
//   )[0][0];
  
//   const se_pred = result.standardError * Math.sqrt(1 + variance_pred);
  
//   // t値（簡易的な近似）
//   const df = n - result.coefficients.length;
//   const t_value = Math.abs(inverseTCDF(alpha / 2, df));
  
//   return {
//     prediction,
//     lower: prediction - t_value * se_pred,
//     upper: prediction + t_value * se_pred
//   };
// }

/**
 * t分布の逆関数（改善版）
 * 数値的により安定な実装
 */
function inverseTCDF(p: number, df: number): number {
  // 入力値の検証
  if (p <= 0 || p >= 1) {
    throw new Error('Probability must be between 0 and 1');
  }
  // 大きな自由度では標準正規分布で近似
  if (df > 30) {
    // 標準正規分布の逆関数の近似
    const a = [2.50662823884, -18.61500062529, 41.39119773534, -25.44106049637];
    const b = [-8.47351093090, 23.08336743743, -21.06224101826, 3.13082909833];
    
    const x = p - 0.5;
    const r = x * x;
    
    const num = ((a[3] * r + a[2]) * r + a[1]) * r + a[0];
    const den = (((b[3] * r + b[2]) * r + b[1]) * r + b[0]) * r + 1;
    
    return x * num / den;
  }
  
  // 小さい自由度の場合の近似
  // 標準正規分布の逆関数の近似を直接使用
  const a = [2.50662823884, -18.61500062529, 41.39119773534, -25.44106049637];
  const b = [-8.47351093090, 23.08336743743, -21.06224101826, 3.13082909833];
  
  const x = p - 0.5;
  const r = x * x;
  
  const num = ((a[3] * r + a[2]) * r + a[1]) * r + a[0];
  const den = (((b[3] * r + b[2]) * r + b[1]) * r + b[0]) * r + 1;
  
  const z = x * num / den;
  return z * Math.sqrt(df / (df - 2));
}

/**
 * F分布の累積分布関数の逆関数（簡易近似）
 * F統計量のp値計算用
 */
function inverseFCDF(f: number, _df1: number, _df2: number): number {
  // 簡易的な近似として、F > 4の場合はp < 0.05とみなす
  if (f > 10) return 0.001;
  if (f > 4) return 0.05;
  if (f > 2) return 0.1;
  if (f > 1) return 0.25;
  return 0.5;
}

/**
 * 単回帰分析用のヘルパー関数
 */
export function simpleLinearRegression(
  x: number[],
  y: number[]
): LinearRegressionResult {
  const X = x.map(xi => [xi]);
  return linearRegression(X, y, true);
}